import { Link, Navigate, useParams } from "react-router-dom";
import IconTile from "../IconTile";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowUpRight, Check, ChevronRight, Palette, IndianRupee, LayoutDashboard } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CredibilityStrip from "./CredibilityStrip";
import { AccentBadge } from "./FeaturesPage";
import SEO, { SITE_URL } from "../SEO";
import { Reveal, Overline } from "../Reveal";
import { Button } from "../ui/button";
import { FEATURE_CATEGORIES, featurePath, getFeatureBySlug, displayImage } from "../../data/features";
import { SIGNUP_URL, PLAN_LOW_PRICE } from "../../data/content";
import { FAQ_SEGMENTS } from "../../data/faqs";
import { productFaqs } from "../../data/pageFaqs";
import { FaqSection } from "../FaqList";
import {
  USER_TYPES,
  getProductKB,
  getProductKBByFeatureId,
  problemsForProduct,
  industriesForProduct,
} from "../../data/knowledge";

function FeatureImage({ cat }) {
  const c = cat.accent.color;
  return (
    <div data-testid="feature-visual" className="relative mx-auto max-w-[540px]">
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-8 rounded-[48px] blur-3xl opacity-[0.18]"
        style={{ background: c }}
      />
      <img
        src={displayImage(cat)}
        alt={`${cat.keyword} - white-label software for agencies by MagicBolt AI`}
        width="1260"
        height="1260"
        loading="eager"
        decoding="async"
        className="relative block w-full h-auto object-contain rounded-[32px] drop-shadow-[0_28px_50px_rgba(11,21,51,0.16)]"
      />
    </div>
  );
}

function SyntheticVisual({ cat }) {
  const c = cat.accent.color;
  return (
    <div
      data-testid="feature-visual"
      className="relative rounded-2xl border border-[#E6E9F1] bg-white overflow-hidden mb-card-shadow"
    >
      {/* header strip */}
      <div className="flex items-center justify-between px-5 h-12 border-b border-[#E6E9F1] bg-[#F4F6FB]">
        <div className="flex items-center gap-2.5">
          <AccentBadge accent={cat.accent} size="w-8 h-8" ic="w-4 h-4" />
          <span className="text-sm font-semibold text-[#0B1533]">{cat.title}</span>
        </div>
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#E6E9F1]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#E6E9F1]" />
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
        </div>
      </div>

      {/* body */}
      <div className="relative p-6">
        <div
          className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-30"
          style={{ background: c }}
        />
        <div className="relative">
          <div className="flex items-center gap-4 mb-5">
            <AccentBadge accent={cat.accent} />
            <div>
              <div className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: c }}>
                {cat.tag}
              </div>
              <div className="text-lg font-bold text-[#0B1533]">Automated</div>
            </div>
          </div>

          <div className="space-y-3">
            {cat.points.map((p) => (
              <div
                key={p.title}
                className="flex items-center gap-3 rounded-lg border border-[#E6E9F1] bg-[#F4F6FB] px-4 py-3"
              >
                <span
                  className="grid place-items-center w-8 h-8 rounded-lg shrink-0"
                  style={{ background: cat.accent.tint || "rgba(255,107,0,0.1)" }}
                >
                  <p.icon className="w-4 h-4" style={{ color: cat.accent.gradient ? "#DD2A7B" : c }} />
                </span>
                <span className="text-sm font-medium text-[#0B1533] truncate">{p.title}</span>
                {cat.comingSoon ? (
                  <span className="ml-auto text-[11px] font-semibold text-[#94A3B8]">soon</span>
                ) : (
                  <span className="ml-auto flex items-center gap-1 text-[11px] font-semibold" style={{ color: c }}>
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: c }} />
                    live
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const RESELL_POINTS = [
  {
    icon: Palette,
    title: "Your brand, your domain",
    desc: "Your logo, colours and login page on your own domain. Clients never see MagicBolt AI.",
  },
  {
    icon: IndianRupee,
    title: "You set the price",
    desc: "Package it into your own plans and keep 100% of what clients pay you.",
  },
  {
    icon: LayoutDashboard,
    title: "One login for every client",
    desc: "Run every client account from one agency dashboard instead of juggling separate tools.",
  },
];

function PointsGrid({ points }) {
  return (
    <ul className="mt-10 grid gap-5 md:grid-cols-2">
      {points.map((p) => (
        <li
          key={p.title}
          data-testid="feature-point"
          className="group flex gap-4 rounded-2xl border border-[#E6E9F1] bg-white p-6 mb-card-shadow transition-all duration-300 hover:-translate-y-1 hover:border-[#FF6B00]/30 hover:shadow-[0_16px_36px_rgba(255,107,0,0.12)]"
        >
          <IconTile icon={p.icon} />
          <div>
            <h3 className="text-base font-semibold text-[#0B1533]">{p.title}</h3>
            <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{p.desc}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function RelatedCard({ cat }) {
  return (
    <Link
      to={featurePath(cat)}
      data-testid="related-feature"
      className="group flex items-center gap-3 rounded-xl border border-[#E6E9F1] bg-white p-4 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(11,21,51,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]/50"
    >
      <AccentBadge accent={cat.accent} size="w-10 h-10" ic="w-5 h-5" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#0B1533]">{cat.title}</div>
        <div className="text-xs text-[#94A3B8]">{cat.tag}</div>
      </div>
      <ArrowUpRight className="h-4 w-4 shrink-0 text-[#CBD2E0] transition-colors group-hover:text-[#FF6B00]" />
    </Link>
  );
}

// Optional business-opportunity block for features that define `promo` in
// features.js: a vertical (9:16) video beside the resale pitch.
function BusinessPromo({ promo }) {
  const title = `${promo.titleLead} ${promo.titleAccent} ${promo.titleTail}`;
  return (
    <section
      data-testid="feature-business-promo"
      className="border-b border-[#E6E9F1] bg-gradient-to-br from-[#FFF7ED] via-white to-[#EFF6FF] py-16 md:py-20"
    >
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 md:grid-cols-[0.9fr_1.1fr] md:gap-12 md:px-12 lg:px-8">
        <Reveal className="mx-auto w-full max-w-[300px] md:max-w-[360px]">
          <div className="rounded-3xl border border-[#E6E9F1] bg-white p-2 shadow-[0_18px_50px_rgba(11,21,51,0.12)]">
            <div className="relative aspect-[9/16] overflow-hidden rounded-2xl bg-[#0B1533]">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${promo.videoId}`}
                title={title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute inset-0 h-full w-full border-0"
              />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="min-w-0">
          <Overline>{promo.badge}</Overline>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tighter leading-tight text-[#0B1533]">
            {promo.titleLead} <span className="block text-[#FF6B00]">{promo.titleAccent}</span> {promo.titleTail}
          </h2>
          <p className="mt-4 text-base text-[#475569] leading-relaxed">{promo.description}</p>
          <ul className="mt-6 space-y-3">
            {promo.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm font-semibold text-[#334155] leading-relaxed">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#DCFCE7]">
                  <Check className="h-3 w-3 text-[#15803D]" strokeWidth={3} />
                </span>
                {b}
              </li>
            ))}
          </ul>
          <Button
            data-testid="feature-business-promo-cta"
            size="lg"
            className="mt-8 w-full sm:w-auto bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
            asChild
          >
            <a href={SIGNUP_URL}>{promo.cta} <ArrowRight className="ml-2 w-4 h-4" /></a>
          </Button>
          <p className="mt-4 text-xs text-[#64748B]">{promo.note}</p>
        </Reveal>
      </div>
    </section>
  );
}

export default function FeatureDetailPage() {
  const { slug } = useParams();
  const cat = getFeatureBySlug(slug);
  if (!cat) return <Navigate to="/features" replace />;

  const path = featurePath(cat);
  const kb = getProductKBByFeatureId(cat.id);
  const problems = kb ? problemsForProduct(kb.id) : [];
  const industries = kb ? industriesForProduct(kb.id) : [];
  const users = kb ? USER_TYPES.filter((u) => kb.forUsers.includes(u.id)) : [];
  const faqSegments = kb ? FAQ_SEGMENTS.filter((s) => kb.faqSegments.includes(s.id)) : [];
  const faqs = [
    ...productFaqs(kb),
    ...faqSegments.flatMap((s) => s.items),
  ].filter((f, i, all) => all.findIndex((x) => x.q === f.q) === i);
  // Semantically related products first (from knowledge.js), then the rest.
  const relatedIds = kb
    ? kb.related.map((id) => getProductKB(id)?.featureId).filter(Boolean)
    : [];
  const related = [
    ...relatedIds.map((id) => FEATURE_CATEGORIES.find((c) => c.id === id)).filter(Boolean),
    ...FEATURE_CATEGORIES.filter((c) => c.id !== cat.id && !relatedIds.includes(c.id)),
  ];
  const included = (cat.includes || []).map((id) => FEATURE_CATEGORIES.find((c) => c.id === id));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: `${cat.title} - MagicBolt AI`,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web-based",
    url: `${SITE_URL}${path}`,
    description: kb ? kb.definition : cat.seoDescription,
    featureList: [cat, ...included].flatMap((c) => c.points.map((p) => p.title)),
    ...(cat.image ? { image: `${SITE_URL}${cat.image}` } : {}),
    publisher: { "@type": "Organization", "@id": `${SITE_URL}/#organization`, name: "MagicBolt AI", url: SITE_URL },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "INR",
      lowPrice: PLAN_LOW_PRICE,
      offerCount: 3,
      url: `${SITE_URL}/pricing`,
    },
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="feature-detail-page">
      <SEO
        title={cat.seoTitle}
        description={cat.seoDescription}
        path={path}
        image={cat.image ? `${SITE_URL}${cat.image}` : undefined}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Features", path: "/features" },
          { name: cat.title, path },
        ]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-16 md:pb-20 overflow-hidden bg-white border-b border-[#E6E9F1]">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-24 right-0" />
        <div className="absolute inset-0 mb-grid-lines opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-1.5 text-sm text-[#94A3B8]">
            <Link to="/" className="hover:text-[#0B1533]">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/features" className="hover:text-[#0B1533]">Features</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-[#475569]">{cat.title}</span>
          </nav>

          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="flex items-center gap-3 mb-5">
                <AccentBadge accent={cat.accent} size="w-11 h-11" ic="w-5 h-5" />
                <Overline>{cat.title}</Overline>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.05] text-[#0B1533]">
                {cat.keyword}{" "}
                <span className="mb-gradient-text"><span className="whitespace-nowrap">white-label</span> software for agencies</span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-[#475569] leading-relaxed">
                {cat.intro}
                {!/resell/i.test(cat.intro) && " Resell it to your clients under your own brand, domain and prices."}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="feature-detail-cta"
                  size="lg"
                  className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
                  asChild
                >
                  <a href={SIGNUP_URL}>Start Free Trial <ArrowRight className="ml-2 w-4 h-4" /></a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-[#CBD2E0] bg-white text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-lg h-12 px-7 text-base"
                  asChild
                >
                  <Link to="/pricing">View Pricing</Link>
                </Button>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              {cat.image ? <FeatureImage cat={cat} /> : <SyntheticVisual cat={cat} />}
            </motion.div>
          </div>
        </div>
      </section>

      {cat.promo && <BusinessPromo promo={cat.promo} />}

      {/* At a glance - definition, problems, audience, industries */}
      {kb && (
        <section className="py-16 md:py-20 bg-white border-b border-[#E6E9F1]" data-testid="feature-at-a-glance">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8 grid gap-10 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tighter leading-tight text-[#0B1533]">
                What is {kb.name}?
              </h2>
              <p className="mt-4 text-base text-[#475569] leading-relaxed">{kb.definition}</p>
              <p className="mt-3 text-sm text-[#64748B]">
                Part of {kb.family === "agency-platform" ? "the agency platform" : "Agency OS"}, the MagicBolt AI
                platform agencies rebrand and resell.
              </p>
            </div>
            <div className="lg:col-span-3 grid gap-6 sm:grid-cols-2">
              {problems.length > 0 && (
                <div className="sm:col-span-2">
                  <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#0B1533]">What problems does it solve?</h3>
                  <ul className="mt-3 space-y-2">
                    {problems.map((pr) => (
                      <li key={pr.id} className="text-sm text-[#475569] leading-relaxed">
                        <Link to={`/product-finder#problem-${pr.id}`} className="font-semibold text-[#0B1533] hover:text-[#FF6B00]">
                          {pr.title}
                        </Link>
                        {" - "}
                        {pr.solvedBy.find((s) => s.product === kb.id)?.role}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {users.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#0B1533]">Who is it for?</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-[#475569]">
                    {users.map((u) => (
                      <li key={u.id}>{u.name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {industries.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#0B1533]">Which industries use it?</h3>
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {industries.map((i) => (
                      <li key={i.id}>
                        <Link to={`/product-finder#industry-${i.id}`} className="text-[#475569] hover:text-[#FF6B00]">
                          {i.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* What's included */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tighter leading-tight text-[#0B1533]">
                What can {cat.title} do?
              </h2>
            </div>
            <PointsGrid points={cat.points} />
          </Reveal>

          {/* Other features bundled into this one (e.g. the agency platform) */}
          {included.map((inc) => (
            <Reveal key={inc.id} className="mt-16">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="flex items-center gap-4 max-w-2xl">
                  <AccentBadge accent={inc.accent} size="w-12 h-12" ic="w-6 h-6" />
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tighter leading-tight text-[#0B1533]">
                      What does {inc.title} add?
                    </h2>
                    <p className="mt-1 text-sm md:text-base text-[#475569]">{inc.intro}</p>
                  </div>
                </div>
                <Link to={featurePath(inc)} className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B00] hover:underline">
                  More on {inc.title} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <PointsGrid points={inc.points} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* White-label resell */}
      <section className="py-20 md:py-24 bg-white border-y border-[#E6E9F1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tighter leading-tight text-[#0B1533]">
                Can I resell {cat.title} as <span className="text-[#FF6B00]">my own product?</span>
              </h2>
              <p className="mt-4 text-base md:text-lg text-[#475569] leading-relaxed">
                Yes. {cat.title} is part of Agency OS, the MagicBolt AI automation platform agencies rebrand and resell.
              </p>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {RESELL_POINTS.map((r) => (
                <div key={r.title} className="rounded-2xl border border-[#E6E9F1] bg-[#F4F6FB] p-6">
                  <span className="grid place-items-center w-11 h-11 rounded-lg bg-[#0B1533] text-white">
                    <r.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-[#0B1533]">{r.title}</h3>
                  <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{r.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold">
              <Link to="/agency-os" className="inline-flex items-center gap-1 text-[#FF6B00] hover:underline">
                How white-label Agency OS works <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/how-agencies-earn" className="inline-flex items-center gap-1 text-[#FF6B00] hover:underline">
                See how agencies earn <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tighter text-[#0B1533]">
                What works well with {cat.title}?
              </h2>
              <Link to="/features" className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B00] hover:underline">
                All features <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <RelatedCard key={r.id} cat={r} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQs for this product - product-specific + resale/pricing + its /faq section */}
      <FaqSection title={`Frequently asked questions about ${cat.title}`} items={faqs} id="product-faq">
        <p className="mt-6 text-sm">
          <Link to={faqSegments[0] ? `/faq#${faqSegments[0].id}` : "/faq"} className="font-semibold text-[#FF6B00] hover:underline">
            All FAQs
          </Link>
          <span className="text-[#94A3B8]"> · </span>
          <Link to="/product-finder" className="font-semibold text-[#FF6B00] hover:underline">
            Not sure this is the right tool? Use the Product Finder
          </Link>
        </p>
      </FaqSection>

      {/* CTA */}
      <section className="relative py-24 border-t border-[#E6E9F1] overflow-hidden bg-white">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-20 left-1/2 -translate-x-1/2" />
        <div className="absolute inset-0 mb-radial-glow" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter leading-none text-[#0B1533]">
              Launch {cat.title} under your brand
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#475569] max-w-xl mx-auto leading-relaxed">
              Every feature, one platform, fully white-label. Start your free trial today.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
                asChild
              >
                <a href={SIGNUP_URL}>Start Free Trial <ArrowRight className="ml-2 w-4 h-4" /></a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-[#CBD2E0] bg-white text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-lg h-12 px-7 text-base"
                asChild
              >
                <Link to="/pricing">Compare Plans</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <CredibilityStrip />

      <Footer showCta={false} />
    </div>
  );
}
